import { forwardRef, type HTMLAttributes } from "react";
import { FollowupFieldChoiceActionEnum } from "@/lib/api/generated/casedb";
import { ConfidencePill } from "@/features/catalog_case_review/shared/components/field_review/AiFieldReview";
import { CaseReviewIcon } from "@/features/catalog_case_review/shared/components/field_review/CaseReviewIcon";
import { EMPTY_VALUE_LABEL } from "@/features/catalog_case_review/shared/constants/caseReviewLabels";
import { followupValueText } from "../model/followupValueText";
import { localDateTimeLabel } from "@/features/catalog_case_review/shared/utils/caseReviewDateTime";
import { normalizeConfidence } from "@/features/catalog_case_review/shared/utils/caseReviewFieldState";
import type { CaseFieldEvidence, CaseFieldValue } from "@/features/catalog_case_review/shared/model/caseReview";
import type { FollowupFieldAction, FollowupProposalView } from "../types/followupReviewView";
import {
  FOLLOWUP_ACCEPT_LABEL, FOLLOWUP_CURRENT_SOURCE_LABEL, FOLLOWUP_CURRENT_VALUE_LABEL,
  FOLLOWUP_DECISION_LABELS, FOLLOWUP_EDIT_LABEL, FOLLOWUP_INCOMING_VALUE_LABEL, FOLLOWUP_KEEP_LABEL,
  FOLLOWUP_SOURCE_LABEL, followupArrivalLabel,
} from "../constants/followupReviewLabels";

interface Props extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
  proposals: FollowupProposalView[];
  readOnly: boolean;
  onChoose(item: FollowupProposalView, action: FollowupFieldAction): void;
  onEdit(item: FollowupProposalView, value: CaseFieldValue): void;
  onShowSource(item: FollowupProposalView, which: "incoming" | "current"): void;
  onClose(): void;
}

function pillEvidence(confidence: string | undefined, reasoning: string | undefined): CaseFieldEvidence | undefined {
  if (!confidence) return undefined;
  return { confidence: normalizeConfidence(confidence), quote: "", reasoning: reasoning ?? "" };
}

/** One proposal's Incoming/Current values and, when it is still decidable, the three actions. */
function ProposalValues({ item, readOnly, onChoose, onEdit, onShowSource }: {
  item: FollowupProposalView; readOnly: boolean;
  onChoose(item: FollowupProposalView, action: FollowupFieldAction): void;
  onEdit(item: FollowupProposalView, value: CaseFieldValue): void;
  onShowSource(item: FollowupProposalView, which: "incoming" | "current"): void;
}) {
  const draftAction = item.draft?.action;
  return <div className="cr-followup-popover-item">
    <div className="cr-followup-popover-head">
      <span className="cr-followup-arrival-tag">{followupArrivalLabel(item.arrivalOrdinal)}</span>
      <span className="cr-followup-decision" data-decision={item.decision}>{FOLLOWUP_DECISION_LABELS[item.decision]}</span>
    </div>
    {item.decidedByName && <p className="cr-followup-decided-by">
      {item.decidedByName}{item.decidedAt ? ` · ${localDateTimeLabel(item.decidedAt) ?? item.decidedAt}` : ""}
    </p>}
    <div className="cr-followup-popover-values">
      <div>
        <small>{FOLLOWUP_INCOMING_VALUE_LABEL}</small>
        <span>{followupValueText(item, item.incomingValue)}</span>
        {pillEvidence(item.incomingEvidence?.confidence, item.incomingEvidence?.reasoning) && <ConfidencePill open={false} tabIndex={-1}
          evidence={pillEvidence(item.incomingEvidence?.confidence, item.incomingEvidence?.reasoning)} />}
        {item.incomingEvidence?.reasoning && <p>{item.incomingEvidence.reasoning}</p>}
        {item.incomingEvidence?.provenance && <button type="button" className="cr-popover-source"
          onClick={() => onShowSource(item, "incoming")}><CaseReviewIcon name="file" size={13} />{FOLLOWUP_SOURCE_LABEL}</button>}
      </div>
      <div>
        <small>{FOLLOWUP_CURRENT_VALUE_LABEL}</small>
        <span>{item.currentPresent ? followupValueText(item, item.currentValue) : EMPTY_VALUE_LABEL}</span>
        {item.currentEvidence?.provenance && <button type="button" className="cr-popover-source"
          onClick={() => onShowSource(item, "current")}><CaseReviewIcon name="file" size={13} />{FOLLOWUP_CURRENT_SOURCE_LABEL}</button>}
      </div>
    </div>
    {item.decidable && !readOnly && <div className="cr-followup-popover-actions">
      <button type="button" className="cr-button" data-active={draftAction === FollowupFieldChoiceActionEnum.Accepted}
        onClick={() => onChoose(item, FollowupFieldChoiceActionEnum.Accepted)}><CaseReviewIcon name="check" size={13} strokeWidth={2.6} />{FOLLOWUP_ACCEPT_LABEL}</button>
      <button type="button" className="cr-button" data-active={draftAction === FollowupFieldChoiceActionEnum.Kept}
        onClick={() => onChoose(item, FollowupFieldChoiceActionEnum.Kept)}>{FOLLOWUP_KEEP_LABEL}</button>
      <button type="button" className="cr-button" data-active={draftAction === FollowupFieldChoiceActionEnum.Edited}
        onClick={() => onEdit(item, item.incomingValue)}>{FOLLOWUP_EDIT_LABEL}</button>
    </div>}
  </div>;
}

export const FollowupProposalPopover = forwardRef<HTMLDivElement, Props>(function FollowupProposalPopover(
  { proposals, readOnly, onChoose, onEdit, onShowSource, onClose, className, ...contentProps }, ref,
) {
  const first = proposals[0];
  return <div {...contentProps} ref={ref} className={`cr-portal cr-popover${className ? ` ${className}` : ""}`}
    role="dialog" aria-label={`Follow-up proposals for ${first.label}`}>
    <div className="cr-popover-head">
      <span className="cr-popover-spark"><CaseReviewIcon name="spark" size={15} /></span>
      <div className="cr-popover-title">
        <div className="cr-popover-label">{first.label}</div>
      </div>
      <button type="button" className="cr-popover-close" aria-label="Close" onClick={onClose}>
        <CaseReviewIcon name="x" size={12} strokeWidth={2.2} />
      </button>
    </div>
    {proposals.map((proposal) => <ProposalValues key={proposal.itemId} item={proposal} readOnly={readOnly}
      onChoose={onChoose} onEdit={onEdit} onShowSource={onShowSource} />)}
  </div>;
});
